import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { moNum } from "@/lib/moNum";
import { buildOrderBOM, buildAvailability } from "@/lib/orderBom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TrendingUp, Plus, Pencil, Trash2, ClipboardCheck, Loader2 } from "lucide-react";

const num = (v) => Number(v) || 0;
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s + "T00:00:00");
  return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function ProjectionForm({ initial, products, customers, saving, onSave, onCancel }) {
  const [customerId, setCustomerId] = useState(initial?.customer_id || "");
  const [productId, setProductId] = useState(initial?.product_id || "");
  const [cfgId, setCfgId] = useState(initial?.config_id || "");
  const [designId, setDesignId] = useState(initial?.design_id || "");
  const [qty, setQty] = useState(initial ? String(initial.quantity) : "");
  const [unit, setUnit] = useState(initial?.unit || "cases");
  const [date, setDate] = useState(initial?.expected_date || "");
  const [notes, setNotes] = useState(initial?.notes || "");

  const product = products.find((p) => p.id === productId) || null;
  const designs = useMemo(() => {
    const seen = new Set();
    const out = [];
    (product?.lines || []).forEach((l) => (l.designs || []).forEach((d) => { if (d?.id && !seen.has(d.id)) { seen.add(d.id); out.push(d); } }));
    return out;
  }, [product]);
  const cfgs = product?.pack_configs || [];
  const cfg = cfgs.find((c) => c.id === cfgId) || cfgs[0] || null;
  const cp = num(cfg?.bottles_per_case);
  const bpp = cp * num(cfg?.cases_per_pallet);
  const ppt = num(cfg?.pallets_per_truckload);
  const perUnit = unit === "bottles" ? 1 : unit === "cases" ? cp : unit === "pallets" ? bpp : bpp * ppt;
  const totalBottles = num(qty) * perUnit;

  const onProduct = (v) => {
    const p = products.find((x) => x.id === v);
    setProductId(v);
    setCfgId(p?.pack_configs?.[0]?.id || "");
    setDesignId("");
  };

  const valid = product && cp > 0 && num(qty) > 0 && (designs.length === 0 || !!designId);

  return (
    <div className="space-y-2.5">
      <Select value={customerId || undefined} onValueChange={(v) => setCustomerId(v === "__none" ? "" : v)}>
        <SelectTrigger className={"rounded-sm" + (customerId ? "" : " text-slate-400")} data-testid="projection-customer">
          <SelectValue placeholder="Customer (optional)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none">— No customer —</SelectItem>
          {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}
        </SelectContent>
      </Select>
      <Select value={productId || undefined} onValueChange={onProduct}>
        <SelectTrigger className="rounded-sm" data-testid="projection-product"><SelectValue placeholder="Select a product" /></SelectTrigger>
        <SelectContent>{products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
      </Select>
      <div className="grid grid-cols-2 gap-2">
        {designs.length > 0 ? (
          <Select value={designId || undefined} onValueChange={setDesignId}>
            <SelectTrigger className="rounded-sm" data-testid="projection-design"><SelectValue placeholder="Select a design" /></SelectTrigger>
            <SelectContent>{designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}</SelectContent>
          </Select>
        ) : (
          <Input readOnly value="" placeholder={product ? "No designs" : "Design"} className="rounded-sm bg-slate-50 text-slate-400" />
        )}
        {cfgs.length > 1 ? (
          <Select value={cfg?.id || undefined} onValueChange={setCfgId}>
            <SelectTrigger className="rounded-sm" data-testid="projection-pack"><SelectValue placeholder="Select pack" /></SelectTrigger>
            <SelectContent>{cfgs.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label || `${c.bottles_per_case}-pack`}</SelectItem>))}</SelectContent>
          </Select>
        ) : (
          <Input readOnly value={cp ? `${cp}-pack` : ""} placeholder="Pack" className="rounded-sm bg-slate-50 text-slate-600" />
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input type="text" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Quantity" className="rounded-sm" data-testid="projection-qty" />
        <Select value={unit} onValueChange={setUnit}>
          <SelectTrigger className="rounded-sm" data-testid="projection-unit"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="cases">Cases</SelectItem>
            <SelectItem value="pallets">Pallets</SelectItem>
            <SelectItem value="truckloads">Truckloads</SelectItem>
            <SelectItem value="bottles">Bottles</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm" title="Expected date" data-testid="projection-date" />
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" rows={2} className="rounded-sm" data-testid="projection-notes" />
      {totalBottles > 0 && cp > 0 && (
        <p className="text-[11px] text-slate-500">= {Math.round(totalBottles).toLocaleString()} bottles{bpp ? ` · ${(Math.round((totalBottles / bpp) * 100) / 100).toLocaleString()} pallets` : ""}</p>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" className="rounded-sm" onClick={onCancel} data-testid="projection-cancel">Cancel</Button>
        <Button disabled={!valid || saving} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="projection-save"
          onClick={() => onSave({
            customer_id: customerId,
            customer_name: customers.find((c) => c.id === customerId)?.company_name || "",
            product_id: productId, config_id: cfg?.id || "", design_id: designId || "",
            quantity: num(qty), unit, expected_date: date, notes,
          })}>
          {saving ? "Saving…" : initial ? "Save Changes" : "Add Projection"}
        </Button>
      </div>
    </div>
  );
}

export default function ProjectionsSection({ products, customers, onConverted }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { initial } | null
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [convertingId, setConvertingId] = useState(null);
  const [showConverted, setShowConverted] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/projections");
      setItems(data);
    } catch { /* section stays empty */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (dialog?.initial) await api.put(`/projections/${dialog.initial.id}`, payload);
      else await api.post("/projections", payload);
      toast.success(dialog?.initial ? "Projection updated." : "Projection added.");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/projections/${toDelete.id}`);
      toast.success("Projection deleted.");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const convert = async (p) => {
    const product = products.find((x) => x.id === p.product_id);
    if (!product) { toast.error("Product no longer exists"); return; }
    setConvertingId(p.id);
    try {
      const cfgs = product.pack_configs || [];
      const cfg = cfgs.find((c) => c.id === p.config_id) || cfgs[0];
      const bottles = Math.round(p.bottles);
      const { data: draft } = await api.post("/manufacturing-orders", {
        product_id: p.product_id, config_id: cfg?.id || "", case_pack: p.case_pack,
        quantity: bottles, unit: "bottles",
        production_date: p.expected_date || new Date().toISOString().slice(0, 10),
        design_id: p.design_id || null, customer_id: p.customer_id || "", customer_name: p.customer_name || "",
      });
      const { data: comps } = await api.get("/components");
      const compIndex = Object.fromEntries(comps.map((c) => [c.id, c]));
      let reservations = {};
      try { reservations = (await api.get("/components/reservations")).data.by_key || {}; } catch { /* non-blocking */ }
      const cp = num(p.case_pack);
      const bpp = cp * num(cfg?.cases_per_pallet);
      const ppt = num(cfg?.pallets_per_truckload);
      const totalPallets = bpp ? bottles / bpp : 0;
      const bom = buildOrderBOM(product, comps, { cfg, casePackSize: cp, totalCases: cp ? bottles / cp : 0, totalPallets, totalTrucks: ppt && totalPallets ? totalPallets / ppt : 0, designId: p.design_id || "" });
      const { data: placed } = await api.post(`/manufacturing-orders/${draft.id}/place`, {
        bottle_size: product.name || "", ordered_bottles: bottles,
        bom_snapshot: bom, availability: buildAvailability(bom, compIndex, reservations),
      });
      await api.post(`/projections/${p.id}/convert`, { mo_id: draft.id, mo_number: placed?.mwo_number || "" });
      toast.success(`Projection converted — ${moNum(placed?.mwo_number || "")} added to the production queue.`);
      load();
      onConverted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setConvertingId(null); }
  };

  const open = items.filter((p) => p.status !== "converted");
  const converted = items.filter((p) => p.status === "converted");
  const visible = showConverted ? [...open, ...converted] : open;

  const qtyLabel = (p) => {
    const u = p.unit === "truckloads" ? "TL" : p.unit;
    return `${Number(p.quantity).toLocaleString()} ${u} · ${Math.round(p.bottles).toLocaleString()} btl`;
  };

  return (
    <div className="mt-14 border-t-2 border-slate-200 pt-8" data-testid="projections-section">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[#002FA7]" />
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Projections — Expected Demand</h2>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">{open.length} open</span>
        </div>
        <div className="flex items-center gap-2">
          {converted.length > 0 && (
            <button onClick={() => setShowConverted((v) => !v)} className="text-xs font-semibold text-slate-500 hover:text-[#002FA7]" data-testid="toggle-converted-projections">
              {showConverted ? "Hide" : "Show"} converted ({converted.length})
            </button>
          )}
          <Button onClick={() => setDialog({ initial: null })} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="add-projection-button">
            <Plus className="mr-1 h-4 w-4" /> Add Projection
          </Button>
        </div>
      </div>
      <p className="-mt-2 mb-4 text-sm text-slate-500">Expected demand that isn't a customer order yet. Converting an entry creates a projection MO in the production queue; projections themselves never reserve stock.</p>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#002FA7]" /></div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center border border-dashed border-slate-300 px-6 py-10 text-center" data-testid="projections-empty">
          <TrendingUp className="h-8 w-8 text-slate-300" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-slate-500">No open projections. Log expected demand here and convert it to an MO when it's time to produce.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200" data-testid="projections-table">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                <th className="px-3 py-2.5">Customer</th>
                <th className="border-l border-slate-200 px-3 py-2.5">Product / Design</th>
                <th className="border-l border-slate-200 px-3 py-2.5">Pack</th>
                <th className="border-l border-slate-200 px-3 py-2.5">Quantity</th>
                <th className="border-l border-slate-200 px-3 py-2.5">Expected</th>
                <th className="border-l border-slate-200 px-3 py-2.5">Status</th>
                <th className="w-44 border-l border-slate-200 px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} className={`border-b border-slate-100 ${p.status === "converted" ? "bg-slate-50/50 text-slate-400" : "hover:bg-slate-50/60"}`} data-testid={`projection-row-${p.id}`}>
                  <td className="px-3 py-2.5">{p.customer_name || <span className="text-slate-400">—</span>}</td>
                  <td className="border-l border-slate-100 px-3 py-2.5">
                    <span className={p.status === "converted" ? "" : "text-slate-800"}>{p.product_name}</span>
                    <span className="ml-1.5 text-xs text-slate-500">{p.design_name || ""}</span>
                  </td>
                  <td className="border-l border-slate-100 px-3 py-2.5">{Math.round(p.case_pack)}-Pack</td>
                  <td className="whitespace-nowrap border-l border-slate-100 px-3 py-2.5 tabular-nums" data-testid={`projection-qty-${p.id}`}>{qtyLabel(p)}</td>
                  <td className="whitespace-nowrap border-l border-slate-100 px-3 py-2.5">{fmtDate(p.expected_date)}</td>
                  <td className="border-l border-slate-100 px-3 py-2.5">
                    {p.status === "converted" ? (
                      <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700" data-testid={`projection-status-${p.id}`}>
                        Converted {p.converted_mo_number ? `· ${moNum(p.converted_mo_number)}` : ""}
                      </span>
                    ) : (
                      <span className="rounded-sm bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700" data-testid={`projection-status-${p.id}`}>Open</span>
                    )}
                  </td>
                  <td className="border-l border-slate-100 px-3 py-2.5 text-right">
                    {p.status !== "converted" && (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" disabled={convertingId === p.id} onClick={() => convert(p)}
                          className="h-8 rounded-sm bg-[#002FA7] px-2.5 text-xs text-white hover:bg-[#002080]" data-testid={`convert-projection-${p.id}`}>
                          <ClipboardCheck className="mr-1 h-3.5 w-3.5" /> {convertingId === p.id ? "Converting…" : "Create MO"}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Edit projection" onClick={() => setDialog({ initial: p })} data-testid={`edit-projection-${p.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" title="Delete projection" onClick={() => setToDelete(p)} data-testid={`delete-projection-${p.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="projection-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{dialog?.initial ? "Edit Projection" : "Add Projection"}</DialogTitle>
            <DialogDescription>Expected demand — converts to a Manufacturing Order when you're ready to produce.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <ProjectionForm initial={dialog.initial} products={products} customers={customers}
              saving={saving} onSave={save} onCancel={() => setDialog(null)} />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-projection-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this projection?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `${toDelete.product_name}${toDelete.design_name ? ` — ${toDelete.design_name}` : ""} · ${qtyLabel(toDelete)}. This only removes the expected-demand entry; no MO or stock is affected.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-projection-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="delete-projection-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
